import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { fetchDeck, login, saveDeck, resetDeck, getToken, clearToken } from "../lib/api";
import { ArrowLeft, Save, RotateCcw, LogOut, Plus, Trash2, Lock } from "lucide-react";

const Field = ({ label, value, onChange, textarea, testid }) => (
  <label className="block">
    <span className="mono mb-1 block text-[0.65rem] uppercase tracking-widest text-stone">{label}</span>
    {textarea ? (
      <textarea
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        className="w-full resize-y border border-ink bg-surface px-3 py-2 text-sm outline-none focus:bg-canary/10"
      />
    ) : (
      <input
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-ink bg-surface px-3 py-2 text-sm outline-none focus:bg-canary/10"
      />
    )}
  </label>
);

function LoginGate({ onAuthed }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(password);
      toast.success("Welcome, steward.");
      onAuthed();
    } catch {
      toast.error("Incorrect password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper grain px-6">
      <form onSubmit={submit} className="relative w-full max-w-sm border border-ink bg-surface p-8" data-testid="admin-login-form">
        <div className="mb-6 flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center bg-canary"><Lock className="h-4 w-4" /></span>
          <div>
            <div className="font-display text-xl font-bold tracking-tight">Content Editor</div>
            <div className="overline text-stone">The Canary Report</div>
          </div>
        </div>
        <Field label="Password" value={password} onChange={setPassword} testid="admin-password-input" />
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-button"
          className="mono mt-5 w-full bg-ink px-4 py-3 text-xs uppercase tracking-widest text-paper transition-colors hover:bg-night disabled:opacity-50"
        >
          {loading ? "Unlocking…" : "Enter"}
        </button>
        <Link to="/" className="mono mt-4 block text-center text-[0.65rem] uppercase tracking-widest text-stone hover:text-ink">
          ← Back to deck
        </Link>
      </form>
    </div>
  );
}

const ArrayEditor = ({ title, list, fields, onChange, sIdx, keyName }) => {
  const update = (i, field, val) => {
    const next = list.map((it, idx) => (idx === i ? { ...it, [field]: val } : it));
    onChange(next);
  };
  const add = () => {
    const blank = fields.reduce((a, f) => ({ ...a, [f.key]: "" }), {});
    onChange([...(list || []), blank]);
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));

  return (
    <div className="mt-4">
      <div className="mb-2 flex items-center justify-between">
        <span className="mono text-[0.65rem] uppercase tracking-widest text-ink">{title}</span>
        <button onClick={add} data-testid={`add-${keyName}-${sIdx}`} className="flex items-center gap-1 border border-ink px-2 py-1 text-[0.65rem] hover:bg-canary">
          <Plus className="h-3 w-3" /> Add
        </button>
      </div>
      <div className="space-y-2">
        {(list || []).map((it, i) => (
          <div key={i} className="relative border border-line bg-paper p-3">
            <button onClick={() => remove(i)} className="absolute right-2 top-2 text-stone hover:text-destructive" data-testid={`remove-${keyName}-${sIdx}-${i}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
            <div className="grid gap-2 pr-6">
              {fields.map((f) => (
                <Field key={f.key} label={f.label} value={it[f.key]} onChange={(v) => update(i, f.key, v)} textarea={f.textarea} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const StringArrayEditor = ({ title, list, onChange, sIdx }) => (
  <div className="mt-4">
    <div className="mb-2 flex items-center justify-between">
      <span className="mono text-[0.65rem] uppercase tracking-widest text-ink">{title}</span>
      <button onClick={() => onChange([...(list || []), ""])} data-testid={`add-partner-${sIdx}`} className="flex items-center gap-1 border border-ink px-2 py-1 text-[0.65rem] hover:bg-canary">
        <Plus className="h-3 w-3" /> Add
      </button>
    </div>
    <div className="space-y-2">
      {(list || []).map((v, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            value={v}
            onChange={(e) => onChange(list.map((x, idx) => (idx === i ? e.target.value : x)))}
            className="w-full border border-ink bg-surface px-3 py-2 text-sm outline-none focus:bg-canary/10"
          />
          <button onClick={() => onChange(list.filter((_, idx) => idx !== i))} className="text-stone hover:text-destructive">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  </div>
);

function Editor({ deck, setDeck, onSaved }) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const setSlide = (idx, patch) => {
    setDeck((d) => ({
      ...d,
      slides: d.slides.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const saved = await saveDeck(deck);
      setDeck(saved);
      onSaved(saved);
      toast.success("Deck content saved.");
    } catch {
      toast.error("Save failed — please log in again.");
    } finally {
      setSaving(false);
    }
  };

  const doReset = async () => {
    try {
      const fresh = await resetDeck();
      setDeck(fresh);
      onSaved(fresh);
      toast.success("Reset to default content.");
    } catch {
      toast.error("Reset failed.");
    }
  };

  const logout = () => { clearToken(); navigate("/"); };

  return (
    <div className="min-h-screen bg-paper grain">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-ink bg-surface px-6 py-4">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-1 text-sm hover:text-canary" data-testid="back-to-deck">
            <ArrowLeft className="h-4 w-4" /> <span className="mono text-xs uppercase tracking-widest">Deck</span>
          </Link>
          <span className="font-display text-lg font-bold tracking-tight">Content Editor</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={doReset} data-testid="reset-button" className="flex items-center gap-1 border border-ink px-3 py-2 text-xs hover:bg-paper">
            <RotateCcw className="h-3.5 w-3.5" /> <span className="mono uppercase tracking-widest">Reset</span>
          </button>
          <button onClick={logout} className="flex items-center gap-1 border border-ink px-3 py-2 text-xs hover:bg-paper">
            <LogOut className="h-3.5 w-3.5" /> <span className="mono uppercase tracking-widest">Logout</span>
          </button>
          <button onClick={save} disabled={saving} data-testid="save-deck-button" className="flex items-center gap-2 bg-ink px-4 py-2 text-xs text-paper hover:bg-night disabled:opacity-50">
            <Save className="h-4 w-4" /> <span className="mono uppercase tracking-widest">{saving ? "Saving…" : "Save"}</span>
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-6 py-10">
        <section className="mb-8 border border-ink bg-surface p-5">
          <div className="mono mb-3 text-xs uppercase tracking-widest text-canary" style={{ WebkitTextStroke: "0.4px #111" }}>Brand</div>
          <div className="grid gap-3">
            <Field label="Brand name" value={deck.brand} onChange={(v) => setDeck((d) => ({ ...d, brand: v }))} testid="brand-input" />
            <Field label="Tagline" value={deck.tagline} onChange={(v) => setDeck((d) => ({ ...d, tagline: v }))} testid="tagline-input" />
          </div>
        </section>

        {deck.slides.map((s, idx) => (
          <section key={s.key || idx} className="mb-6 border border-ink bg-surface p-5" data-testid={`edit-slide-${s.key || idx}`}>
            <div className="mb-4 flex items-center gap-2">
              <span className="mono flex h-6 w-6 items-center justify-center bg-canary text-[0.65rem]">{String(idx + 1).padStart(2, "0")}</span>
              <span className="font-display text-lg tracking-tight">{s.nav}</span>
              <span className="mono ml-auto text-[0.6rem] uppercase tracking-widest text-stone">{s.type}</span>
            </div>
            <div className="grid gap-3">
              <Field label="Nav label" value={s.nav} onChange={(v) => setSlide(idx, { nav: v })} />
              <Field label="Overline" value={s.overline} onChange={(v) => setSlide(idx, { overline: v })} />
              <Field label="Title" value={s.title} onChange={(v) => setSlide(idx, { title: v })} textarea />
              <Field label="Subtitle" value={s.subtitle} onChange={(v) => setSlide(idx, { subtitle: v })} textarea />
              {"footnote" in s && <Field label="Footnote" value={s.footnote} onChange={(v) => setSlide(idx, { footnote: v })} />}
            </div>

            {Array.isArray(s.stats) && (
              <ArrayEditor title="Stats" list={s.stats} keyName="stat" sIdx={idx}
                fields={[{ key: "value", label: "Value" }, { key: "label", label: "Label" }]}
                onChange={(next) => setSlide(idx, { stats: next })} />
            )}
            {Array.isArray(s.items) && (
              <ArrayEditor title="Items" list={s.items} keyName="item" sIdx={idx}
                fields={[{ key: "title", label: "Title" }, { key: "desc", label: "Description", textarea: true }]}
                onChange={(next) => setSlide(idx, { items: next })} />
            )}
            {Array.isArray(s.partners) && (
              <StringArrayEditor title="Partners" list={s.partners} sIdx={idx} onChange={(next) => setSlide(idx, { partners: next })} />
            )}
          </section>
        ))}
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!getToken());
  const [deck, setDeck] = useState(null);

  useEffect(() => {
    if (authed) fetchDeck().then(setDeck).catch(() => {});
  }, [authed]);

  if (!authed) return <LoginGate onAuthed={() => setAuthed(true)} />;
  if (!deck) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper">
        <span className="mono animate-pulse text-xs uppercase tracking-[0.3em] text-stone">Loading content…</span>
      </div>
    );
  }
  return <Editor deck={deck} setDeck={setDeck} onSaved={setDeck} />;
}
